import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, MessageSquare, HelpCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BravoSupportChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial welcome message
  useEffect(() => {
    setMessages([{
      id: 'welcome',
      type: 'assistant',
      content: '¡Hola! Soy el asistente de soporte de Bravo Cinema Streaming. ¿En qué puedo ayudarte hoy?\n\nPuedo resolver dudas sobre:\n• Sistema Bravo y sus características\n• Proceso de contratación de contenidos\n• Soporte técnico\n• Tarifas y condiciones',
      timestamp: new Date()
    }]);
  }, []);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Retry logic with exponential backoff
    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Chat attempt ${attempt + 1}/${maxRetries + 1}`);
        
        const response = await fetch(`${BACKEND_URL}/api/chat/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMessage.content,
            session_id: sessionId
          })
        });

        console.log(`Response status: ${response.status}`);

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Server error: ${errorText}`);
          throw new Error(`Error del servidor: ${response.status}`);
        }

        const data = await response.json();
        
        if (!sessionId) {
          setSessionId(data.session_id);
        }

        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          type: 'assistant',
          content: data.response,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        inputRef.current?.focus();
        return; // Success - exit the retry loop
        
      } catch (error) {
        console.error(`Chat error (attempt ${attempt + 1}):`, error);
        lastError = error;
        
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }
    }

    // All retries failed
    console.error('All chat attempts failed:', lastError);
    setMessages(prev => [...prev, {
      id: `error-${Date.now()}`,
      type: 'assistant',
      content: 'Lo siento, ha ocurrido un error. Por favor, intenta de nuevo o contacta con soporte@bravocinemastreaming.es',
      timestamp: new Date(),
      isError: true
    }]);
    setIsLoading(false);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    '¿Qué es Bravo Cinema Streaming?',
    '¿Cómo puedo contratar contenidos?',
    '¿Cuáles son las tarifas?',
    '¿Qué soporte técnico ofrecen?'
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1a1a1a] to-[#0d0d0d] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#e4002b]/20 rounded-full flex items-center justify-center">
              <MessageSquare className="w-7 h-7 text-[#e4002b]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Soporte Bravo</h1>
              <p className="text-white/60 text-sm">Asistente virtual de Bravo Cinema Streaming</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-[#111111] rounded-2xl border border-white/10 overflow-hidden min-h-[600px] flex flex-col">
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ maxHeight: 'calc(100vh - 350px)' }}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-4 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}
              >
                {/* Avatar */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  message.type === 'user' 
                    ? 'bg-blue-500/20' 
                    : message.isError 
                      ? 'bg-red-500/20'
                      : 'bg-[#e4002b]/20'
                }`}>
                  {message.type === 'user' ? (
                    <User className="w-5 h-5 text-blue-400" />
                  ) : (
                    <Bot className={`w-5 h-5 ${message.isError ? 'text-red-400' : 'text-[#e4002b]'}`} />
                  )}
                </div>

                {/* Message Content */}
                <div className={`flex-1 max-w-[80%] ${message.type === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block px-5 py-4 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : message.isError
                          ? 'bg-red-500/10 border border-red-500/30 text-red-200'
                          : 'bg-white/5 text-white/90'
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                  </div>
                  <p className="text-white/30 text-xs mt-2">
                    {message.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#e4002b]/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[#e4002b]" />
                </div>
                <div className="bg-white/5 px-5 py-4 rounded-2xl">
                  <div className="flex items-center gap-2 text-white/60">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Escribiendo...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions (show only at start) */}
          {messages.length === 1 && (
            <div className="px-6 pb-4">
              <p className="text-white/40 text-xs mb-3 flex items-center gap-2">
                <HelpCircle className="w-3 h-3" />
                Preguntas sugeridas
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInputMessage(question);
                      inputRef.current?.focus();
                    }}
                    className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/70 text-sm transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-white/10 p-4 bg-[#0a0a0a]">
            <div className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                disabled={isLoading}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-[#e4002b]/50 disabled:opacity-50 text-sm"
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-6 py-4 bg-[#e4002b] hover:bg-[#c4002b] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-medium transition-colors flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-white/30 text-xs mt-3 text-center">
              Asistente IA de Bravo Cinema Streaming • Para soporte avanzado: soporte@bravocinemastreaming.es
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BravoSupportChat;
